import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CalendarCheck, CaretLeft, CaretRight, Briefcase, Lightning, Certificate, X as XIcon, ListBullets, Plus, VideoCamera, Trash, PencilSimple, Spinner, CheckCircle, FileArrowUp, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ConfirmDelete from "@/components/ConfirmDelete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = ["Google Meet", "Microsoft Teams", "Zoom", "Cisco WebEx", "Altro"];

const TYPE_META = {
  interview:     { label: "Colloqui",      color: "#1B4FD8", bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-700",   icon: Briefcase },
  task:          { label: "Task",          color: "#F59E0B", bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",  icon: Lightning },
  certification: { label: "Certificazioni",color: "#DC2626", bg: "bg-red-50",     border: "border-red-200",     text: "text-red-700",    icon: Certificate },
};

const ITALIAN_MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const ITALIAN_DAYS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function monthFirstDay(y, m) { return new Date(y, m, 1); }
// Monday-first weekday: 0=Mon … 6=Sun
function dowMon(d) { return (d.getDay() + 6) % 7; }

function addDays(date, days) { const d = new Date(date); d.setDate(d.getDate()+days); return d; }

function getMonthGrid(year, month) {
  const first = monthFirstDay(year, month);
  const start = addDays(first, -dowMon(first));
  return Array.from({length: 42}, (_, i) => addDays(start, i));
}

function getWeekGrid(refDate) {
  const start = addDays(refDate, -dowMon(refDate));
  return Array.from({length: 7}, (_, i) => addDays(start, i));
}

const EMPTY_INTERVIEW = { candidate_id: "", title: "", date: "", time: "10:00", duration: 60, platform: "Google Meet", meeting_link: "", notes: "" };
const EMPTY_TASK = { employee_id: "", title: "", description: "", category: "altro", due_date: "" };
const EMPTY_CERT = { employee_id: "", name: "", issuing_body: "", expiry_date: "", no_expiry: false, notes: "", file: null };

const TASK_CATEGORIES = [
  { value: "formazione",     label: "Formazione" },
  { value: "performance",    label: "Performance" },
  { value: "feedback",       label: "Feedback" },
  { value: "salute_benessere", label: "Salute & Benessere" },
  { value: "altro",          label: "Altro" },
];

export default function CalendarHubPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeWorkspaceId } = useAuth();

  const [view, setView] = useState(searchParams.get("view") || "month"); // month|week|day
  const [refDate, setRefDate] = useState(() => new Date());
  const [filters, setFilters] = useState({ interviews: true, tasks: true, certifications: true });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Interview edit dialog state
  const [ivOpen, setIvOpen] = useState(false);
  const [ivEditId, setIvEditId] = useState(null);
  const [ivForm, setIvForm] = useState({ ...EMPTY_INTERVIEW });
  const [ivSaving, setIvSaving] = useState(false);

  // Task creation dialog (new in Batch 63-bis)
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ ...EMPTY_TASK });
  const [taskSaving, setTaskSaving] = useState(false);

  // Certification creation dialog (new in Batch 63-bis)
  const [certOpen, setCertOpen] = useState(false);
  const [certForm, setCertForm] = useState({ ...EMPTY_CERT });
  const [certSaving, setCertSaving] = useState(false);

  // Compute date range for query
  const range = useMemo(() => {
    if (view === "month") {
      const grid = getMonthGrid(refDate.getFullYear(), refDate.getMonth());
      return { from: ymd(grid[0]), to: ymd(grid[grid.length - 1]) };
    }
    if (view === "week") {
      const w = getWeekGrid(refDate);
      return { from: ymd(w[0]), to: ymd(w[6]) };
    }
    return { from: ymd(refDate), to: ymd(refDate) };
  }, [view, refDate]);

  const enabledTypes = useMemo(() => {
    const arr = [];
    if (filters.interviews) arr.push("interviews");
    if (filters.tasks) arr.push("tasks");
    if (filters.certifications) arr.push("certifications");
    return arr.join(",");
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/events`, {
        params: { from: range.from, to: range.to, types: enabledTypes || "none" },
        withCredentials: true,
      });
      setEvents(r.data?.events || []);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sessione scaduta");
      else toast.error("Errore caricamento eventi");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, enabledTypes]);

  useEffect(() => { load(); }, [load, activeWorkspaceId]);

  // Load candidates + employees for the create forms
  useEffect(() => {
    axios.get(`${API}/candidates`, { withCredentials: true })
      .then(r => setCandidates((r.data || []).filter(c => c.status !== "Archiviato")))
      .catch(() => setCandidates([]));
    axios.get(`${API}/employees`, { withCredentials: true })
      .then(r => setEmployees((r.data || []).filter(e => !e.deleted_at)))
      .catch(() => setEmployees([]));
  }, [activeWorkspaceId]);

  // Index events by date for fast lookup
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.date) continue;
      (map[e.date] = map[e.date] || []).push(e);
    }
    Object.values(map).forEach(arr => arr.sort((a,b) => (a.time||"").localeCompare(b.time||"")));
    return map;
  }, [events]);

  // Counters by type
  const countByType = useMemo(() => {
    const c = { interview: 0, task: 0, certification: 0 };
    events.forEach(e => { c[e.type] = (c[e.type] || 0) + 1; });
    return c;
  }, [events]);

  // Navigation handlers
  const goPrev = () => {
    const d = new Date(refDate);
    if (view === "month") d.setMonth(d.getMonth()-1);
    else if (view === "week") d.setDate(d.getDate()-7);
    else d.setDate(d.getDate()-1);
    setRefDate(d);
  };
  const goNext = () => {
    const d = new Date(refDate);
    if (view === "month") d.setMonth(d.getMonth()+1);
    else if (view === "week") d.setDate(d.getDate()+7);
    else d.setDate(d.getDate()+1);
    setRefDate(d);
  };
  const goToday = () => setRefDate(new Date());

  const toggleFilter = (k) => setFilters(f => ({ ...f, [k]: !f[k] }));

  const onEventClick = (ev) => {
    if (ev.type === "interview") {
      // Open inline edit dialog
      const iv = events.find(e => e.id === ev.id);
      const m = iv?.metadata || {};
      setIvForm({
        candidate_id: m.candidate_id || "",
        title: iv.title || "",
        date: iv.date || "",
        time: iv.time || "10:00",
        duration: iv.duration_minutes || 60,
        platform: m.platform || "Google Meet",
        meeting_link: m.meeting_link || "",
        notes: m.notes || "",
      });
      setIvEditId(iv.entity_id);
      setIvOpen(true);
    } else if (ev.type === "task") {
      const empId = ev.metadata?.employee_id;
      if (empId) navigate(`/employees?tab=schede&expand=${empId}`);
    } else if (ev.type === "certification") {
      const empId = ev.metadata?.employee_id;
      if (empId) navigate(`/employees?tab=certificazioni`);
    }
  };

  const newInterview = (presetDate, presetTime) => {
    setIvForm({ ...EMPTY_INTERVIEW, date: presetDate || ymd(refDate), time: presetTime || "10:00" });
    setIvEditId(null);
    setIvOpen(true);
  };
  const newTask = (presetDate) => {
    setTaskForm({ ...EMPTY_TASK, due_date: presetDate || ymd(refDate) });
    setTaskOpen(true);
  };
  const newCert = (presetDate) => {
    setCertForm({ ...EMPTY_CERT, expiry_date: presetDate || ymd(refDate) });
    setCertOpen(true);
  };

  const onEmptyDayClick = (dateStr, timeStr) => {
    // Show quick-select popover via Popover handled in MonthGrid; pass through to handlers
    // This wrapper used by Day view "+ Aggiungi" button
    return { dateStr, timeStr };
  };

  const saveInterview = async () => {
    if (!ivForm.candidate_id || !ivForm.date) { toast.error("Seleziona candidato e data"); return; }
    if (ivSaving) return;
    setIvSaving(true);
    try {
      if (ivEditId) {
        const { data } = await axios.put(`${API}/interviews/${ivEditId}`, ivForm, { withCredentials: true });
        if (data?.email_sent) toast.success("Email aggiornamento inviata al candidato");
        toast.success("Colloquio aggiornato");
      } else {
        const { data } = await axios.post(`${API}/interviews`, ivForm, { withCredentials: true });
        if (data?.email_sent) toast.success("Email con dettagli inviata al candidato");
        toast.success("Colloquio programmato");
      }
      setIvOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setIvSaving(false);
    }
  };

  const saveTask = async () => {
    if (!taskForm.employee_id || !taskForm.title?.trim()) { toast.error("Seleziona dipendente e inserisci un titolo"); return; }
    if (taskSaving) return;
    setTaskSaving(true);
    try {
      await axios.post(`${API}/employees/${taskForm.employee_id}/action-plan`, {
        title: taskForm.title.trim(),
        category: taskForm.category || "altro",
        description: taskForm.description?.trim() || null,
        due_date: taskForm.due_date || null,
      }, { withCredentials: true });
      const emp = employees.find(e => e.id === taskForm.employee_id);
      const ename = emp ? `${emp.first_name} ${emp.last_name}`.trim() : "dipendente";
      toast.success(`Task creato per ${ename}`, {
        action: { label: "Vai a scheda", onClick: () => navigate(`/employees?tab=schede&expand=${taskForm.employee_id}`) },
      });
      setTaskOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione task");
    } finally {
      setTaskSaving(false);
    }
  };

  const saveCert = async () => {
    if (!certForm.employee_id || !certForm.name?.trim()) { toast.error("Seleziona dipendente e inserisci nome"); return; }
    if (!certForm.no_expiry && !certForm.expiry_date) { toast.error("Data scadenza obbligatoria (o spunta 'Senza scadenza')"); return; }
    if (certForm.file && certForm.file.size > 10 * 1024 * 1024) { toast.error("File > 10MB"); return; }
    if (certSaving) return;
    setCertSaving(true);
    try {
      const { data: cert } = await axios.post(`${API}/employees/${certForm.employee_id}/certifications-v2`, {
        name: certForm.name.trim(),
        issuing_body: certForm.issuing_body?.trim() || "",
        expiry_date: certForm.expiry_date || null,
        no_expiry: !!certForm.no_expiry,
        notes: certForm.notes?.trim() || null,
      }, { withCredentials: true });
      // Optional file upload
      if (certForm.file && cert?.id) {
        const fd = new FormData();
        fd.append("file", certForm.file);
        try {
          await axios.post(`${API}/certifications-v2/${cert.id}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          toast.error("Cert creata ma upload file fallito");
        }
      }
      const emp = employees.find(e => e.id === certForm.employee_id);
      const ename = emp ? `${emp.first_name} ${emp.last_name}`.trim() : "dipendente";
      toast.success(`Certificazione creata per ${ename}`);
      setCertOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione certificazione");
    } finally {
      setCertSaving(false);
    }
  };

  const deleteInterview = async () => {
    if (!ivEditId) return;
    try {
      await axios.delete(`${API}/interviews/${ivEditId}`, { withCredentials: true });
      toast.success("Colloquio cancellato · email inviata al candidato");
      setIvOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const headerLabel = useMemo(() => {
    if (view === "month") return `${ITALIAN_MONTHS[refDate.getMonth()]} ${refDate.getFullYear()}`;
    if (view === "week") {
      const w = getWeekGrid(refDate);
      return `${w[0].getDate()} ${ITALIAN_MONTHS[w[0].getMonth()].slice(0,3)} – ${w[6].getDate()} ${ITALIAN_MONTHS[w[6].getMonth()].slice(0,3)} ${w[6].getFullYear()}`;
    }
    return refDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [view, refDate]);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="calendar-hub-page">
      <PageHeader title="Calendario" subtitle="Vista unificata di colloqui, task del piano d'azione e scadenze certificazioni" />

      {/* Sticky toolbar */}
      <div className="sticky top-0 z-10 bg-[#F4F7FB] -mx-6 px-6 py-3 border-b border-[#0D1F3C]/8" data-testid="calendar-toolbar">
        <div className="flex items-center gap-3 flex-wrap">
          {/* View switcher */}
          <div className="flex items-center bg-white border border-[#0D1F3C]/10 rounded-md p-0.5">
            {[["month","Mese"],["week","Settimana"],["day","Giorno"]].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded ${view === v ? "bg-[#1B4FD8] text-white" : "text-[#0D1F3C]/70 hover:bg-[#0D1F3C]/5"}`}
                data-testid={`view-${v}`}>{label}</button>
            ))}
          </div>

          {/* Nav arrows */}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={goPrev} className="h-9 w-9 p-0" data-testid="nav-prev"><CaretLeft size={14} /></Button>
            <Button size="sm" variant="outline" onClick={goToday} className="h-9 px-3 text-xs" data-testid="nav-today">Oggi</Button>
            <Button size="sm" variant="outline" onClick={goNext} className="h-9 w-9 p-0" data-testid="nav-next"><CaretRight size={14} /></Button>
          </div>

          {/* Current label */}
          <h2 className="text-base font-semibold text-[#0D1F3C] capitalize ml-2" data-testid="header-label">{headerLabel}</h2>

          {/* Filter chips */}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {Object.entries(TYPE_META).map(([key, m]) => {
              const filterKey = key === "interview" ? "interviews" : key === "task" ? "tasks" : "certifications";
              const active = filters[filterKey];
              const count = countByType[key] || 0;
              return (
                <button key={key} onClick={() => toggleFilter(filterKey)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    active ? `${m.bg} ${m.border} ${m.text}` : "bg-white border-[#0D1F3C]/10 text-[#0D1F3C]/40"
                  }`}
                  data-testid={`filter-${filterKey}`}>
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: active ? m.color : "#cbd5e1" }} />
                  {m.label}
                  <span className={`text-[10px] ${active ? "opacity-80" : "opacity-50"}`}>({count})</span>
                </button>
              );
            })}
            <Button size="sm" variant="outline" onClick={() => navigate("/calendario/lista")} className="h-9 gap-1.5 text-xs" data-testid="open-list-view">
              <ListBullets size={14} /> Lista colloqui
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-9 gap-1.5 bg-[#1B4FD8] hover:bg-[#0D1F3C] text-xs" data-testid="new-event-btn">
                  <Plus size={14} weight="bold" /> Nuovo <CaretDown size={12} weight="bold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => newInterview()} className="gap-2 cursor-pointer" data-testid="new-interview-menu">
                  <Briefcase size={16} weight="duotone" style={{ color: TYPE_META.interview.color }} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Nuovo colloquio</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => newTask()} className="gap-2 cursor-pointer" data-testid="new-task-menu">
                  <CheckCircle size={16} weight="duotone" style={{ color: TYPE_META.task.color }} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Nuovo task</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => newCert()} className="gap-2 cursor-pointer" data-testid="new-cert-menu">
                  <FileArrowUp size={16} weight="duotone" style={{ color: TYPE_META.certification.color }} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Nuova certificazione</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Calendar body */}
      {loading ? (
        <Card className="p-12 text-center text-sm text-[#0D1F3C]/50">Caricamento eventi…</Card>
      ) : view === "month" ? (
        <MonthGrid refDate={refDate} eventsByDate={eventsByDate} onEventClick={onEventClick}
          onCreateInterview={newInterview} onCreateTask={newTask} onCreateCert={newCert} />
      ) : view === "week" ? (
        <WeekGrid refDate={refDate} eventsByDate={eventsByDate} onEventClick={onEventClick}
          onCreateInterview={newInterview} onCreateTask={newTask} onCreateCert={newCert} />
      ) : (
        <DayGrid refDate={refDate} events={events.filter(e => e.date === ymd(refDate))} onEventClick={onEventClick}
          onCreateInterview={newInterview} onCreateTask={newTask} onCreateCert={newCert} />
      )}

      {/* Interview edit/create dialog */}
      <Dialog open={ivOpen} onOpenChange={setIvOpen}>
        <DialogContent className="max-w-lg" data-testid="interview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarCheck size={18} weight="duotone" />{ivEditId ? "Modifica colloquio" : "Nuovo colloquio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Candidato *</Label>
              <Select value={ivForm.candidate_id} onValueChange={v => setIvForm(p => ({...p, candidate_id: v}))}>
                <SelectTrigger data-testid="iv-candidate"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{candidates.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Data *</Label><Input type="date" value={ivForm.date} onChange={e=>setIvForm(p=>({...p, date:e.target.value}))} data-testid="iv-date" /></div>
              <div><Label className="text-xs">Ora</Label><Input type="time" value={ivForm.time} onChange={e=>setIvForm(p=>({...p, time:e.target.value}))} data-testid="iv-time" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Durata (min)</Label>
                <Input type="number" value={ivForm.duration} onChange={e=>setIvForm(p=>({...p, duration: parseInt(e.target.value)||60}))} data-testid="iv-duration" />
              </div>
              <div>
                <Label className="text-xs">Piattaforma</Label>
                <Select value={ivForm.platform} onValueChange={v => setIvForm(p=>({...p, platform:v}))}>
                  <SelectTrigger data-testid="iv-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Link meeting</Label>
              <Input value={ivForm.meeting_link} onChange={e=>setIvForm(p=>({...p, meeting_link:e.target.value}))} placeholder="https://…" data-testid="iv-link" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea value={ivForm.notes} onChange={e=>setIvForm(p=>({...p, notes:e.target.value}))} rows={2} data-testid="iv-notes" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {ivEditId && (
              <ConfirmDelete title="Cancellare colloquio?" description="Verrà inviata email di cancellazione al candidato e l'evento rimosso dai calendari sincronizzati." onConfirm={deleteInterview}>
                <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 gap-1 mr-auto" data-testid="iv-delete"><Trash size={14} /> Cancella</Button>
              </ConfirmDelete>
            )}
            <Button variant="outline" onClick={() => setIvOpen(false)} disabled={ivSaving} data-testid="iv-cancel">Annulla</Button>
            <Button onClick={saveInterview} disabled={ivSaving} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] gap-1.5" data-testid="iv-save">
              {ivSaving ? <Spinner size={14} className="animate-spin" /> : <PencilSimple size={14} weight="bold" />}
              {ivSaving ? "Salvataggio…" : (ivEditId ? "Salva modifiche" : "Crea colloquio")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task creation dialog (Batch 63-bis) */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="max-w-lg" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={18} weight="duotone" style={{ color: TYPE_META.task.color }} />
              Nuovo task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Dipendente *</Label>
              <Select value={taskForm.employee_id} onValueChange={v => setTaskForm(p => ({...p, employee_id: v}))}>
                <SelectTrigger data-testid="task-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                <SelectContent>{employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input value={taskForm.title} onChange={e => setTaskForm(p => ({...p, title: e.target.value.slice(0, 250)}))} placeholder="Es. Completare formazione GDPR" data-testid="task-title" />
              <p className="text-[10px] text-[#0D1F3C]/40 mt-0.5">{(taskForm.title || "").length}/250</p>
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Textarea value={taskForm.description} onChange={e => setTaskForm(p => ({...p, description: e.target.value}))} rows={2} maxLength={500} data-testid="task-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data prevista</Label>
                <Input type="date" value={taskForm.due_date} onChange={e => setTaskForm(p => ({...p, due_date: e.target.value}))} data-testid="task-due-date" />
              </div>
              <div>
                <Label className="text-xs">Categoria</Label>
                <Select value={taskForm.category} onValueChange={v => setTaskForm(p => ({...p, category: v}))}>
                  <SelectTrigger data-testid="task-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-[#0D1F3C]/50">Stato iniziale: <strong>Da fare</strong> (i task creati manualmente non richiedono approvazione)</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTaskOpen(false)} disabled={taskSaving} data-testid="task-cancel">Annulla</Button>
            <Button onClick={saveTask} disabled={taskSaving} className="gap-1.5" style={{ background: TYPE_META.task.color }} data-testid="task-save">
              {taskSaving ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
              {taskSaving ? "Creazione…" : "Crea task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certification creation dialog (Batch 63-bis) */}
      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent className="max-w-lg" data-testid="cert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileArrowUp size={18} weight="duotone" style={{ color: TYPE_META.certification.color }} />
              Nuova certificazione
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Dipendente *</Label>
              <Select value={certForm.employee_id} onValueChange={v => setCertForm(p => ({...p, employee_id: v}))}>
                <SelectTrigger data-testid="cert-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                <SelectContent>{employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nome certificazione *</Label>
              <Input value={certForm.name} onChange={e => setCertForm(p => ({...p, name: e.target.value.slice(0, 200)}))} placeholder="Es. HACCP, Sicurezza sul lavoro" data-testid="cert-name" />
            </div>
            <div>
              <Label className="text-xs">Ente emittente</Label>
              <Input value={certForm.issuing_body} onChange={e => setCertForm(p => ({...p, issuing_body: e.target.value.slice(0, 200)}))} placeholder="Es. Ministero della Salute" data-testid="cert-issuing-body" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Data scadenza{!certForm.no_expiry && " *"}</Label>
                <Input type="date" value={certForm.expiry_date} disabled={certForm.no_expiry}
                  onChange={e => setCertForm(p => ({...p, expiry_date: e.target.value}))} data-testid="cert-expiry-date" />
              </div>
              <label className="flex items-center gap-2 text-xs text-[#0D1F3C]/70 pb-2 cursor-pointer">
                <input type="checkbox" checked={certForm.no_expiry}
                  onChange={e => setCertForm(p => ({...p, no_expiry: e.target.checked, expiry_date: e.target.checked ? "" : p.expiry_date}))}
                  data-testid="cert-no-expiry" />
                Senza scadenza
              </label>
            </div>
            <div>
              <Label className="text-xs">File allegato (PDF/JPG/PNG, max 10MB)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={e => setCertForm(p => ({...p, file: e.target.files?.[0] || null}))} data-testid="cert-file" />
              {certForm.file && <p className="text-[10px] text-[#0D1F3C]/50 mt-1">{certForm.file.name} · {(certForm.file.size/1024/1024).toFixed(2)}MB</p>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCertOpen(false)} disabled={certSaving} data-testid="cert-cancel">Annulla</Button>
            <Button onClick={saveCert} disabled={certSaving} className="gap-1.5" style={{ background: TYPE_META.certification.color }} data-testid="cert-save">
              {certSaving ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
              {certSaving ? "Creazione…" : "Crea certificazione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────── Sub-components ───────────────────────

function EventChip({ ev, onClick, compact = true }) {
  const m = TYPE_META[ev.type] || TYPE_META.interview;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      className={`block w-full text-left ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs"} rounded ${m.bg} ${m.text} ${m.border} border truncate hover:opacity-80 transition`}
      style={{ borderLeftWidth: 3, borderLeftColor: m.color }}
      title={`${ev.time ? ev.time + " · " : ""}${ev.title}`}
      data-testid={`event-${ev.id}`}
    >
      {ev.time && <span className="font-mono mr-1 opacity-70">{ev.time}</span>}
      {ev.title}
    </button>
  );
}

function QuickCreatePopover({ dateStr, onCreateInterview, onCreateTask, onCreateCert, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-56 p-1" data-testid={`quick-popover-${dateStr}`}>
        <button onClick={() => { setOpen(false); onCreateInterview(dateStr); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#0D1F3C]/5 rounded text-left" data-testid="quick-new-interview">
          <Briefcase size={16} weight="duotone" style={{ color: TYPE_META.interview.color }} /> Nuovo colloquio
        </button>
        <button onClick={() => { setOpen(false); onCreateTask(dateStr); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#0D1F3C]/5 rounded text-left" data-testid="quick-new-task">
          <CheckCircle size={16} weight="duotone" style={{ color: TYPE_META.task.color }} /> Nuovo task
        </button>
        <button onClick={() => { setOpen(false); onCreateCert(dateStr); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#0D1F3C]/5 rounded text-left" data-testid="quick-new-cert">
          <FileArrowUp size={16} weight="duotone" style={{ color: TYPE_META.certification.color }} /> Nuova certificazione
        </button>
      </PopoverContent>
    </Popover>
  );
}

function MonthGrid({ refDate, eventsByDate, onEventClick, onCreateInterview, onCreateTask, onCreateCert }) {
  const days = useMemo(() => getMonthGrid(refDate.getFullYear(), refDate.getMonth()), [refDate]);
  const todayStr = ymd(new Date());
  const currentMonth = refDate.getMonth();
  return (
    <Card className="border-[#0D1F3C]/8 overflow-hidden" data-testid="month-grid">
      <div className="grid grid-cols-7 bg-[#0D1F3C]/5 border-b border-[#0D1F3C]/8">
        {ITALIAN_DAYS.map(d => <div key={d} className="px-2 py-2 text-[11px] font-semibold text-[#0D1F3C]/60 text-center uppercase tracking-wider">{d}</div>)}
      </div>
      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(110px, 1fr)" }}>
        {days.map((d, i) => {
          const ds = ymd(d);
          const evs = eventsByDate[ds] || [];
          const otherMonth = d.getMonth() !== currentMonth;
          const isToday = ds === todayStr;
          return (
            <QuickCreatePopover key={i} dateStr={ds}
              onCreateInterview={onCreateInterview} onCreateTask={onCreateTask} onCreateCert={onCreateCert}>
              <div
                className={`border-r border-b border-[#0D1F3C]/8 p-1.5 cursor-pointer ${otherMonth ? "bg-[#0D1F3C]/2" : "bg-white"} ${isToday ? "ring-2 ring-[#1B4FD8] ring-inset" : ""} hover:bg-[#1B4FD8]/5 transition-colors`}
                data-testid={`day-${ds}`}>
                <div className={`text-[11px] mb-1 ${otherMonth ? "text-[#0D1F3C]/30" : isToday ? "text-[#1B4FD8] font-bold" : "text-[#0D1F3C]/60"}`}>
                  {d.getDate()}
                </div>
                <div className="space-y-0.5">
                  {evs.slice(0, 4).map(ev => <EventChip key={ev.id} ev={ev} onClick={onEventClick} compact />)}
                  {evs.length > 4 && <div className="text-[10px] text-[#0D1F3C]/40 px-1">+ {evs.length - 4} altri</div>}
                </div>
              </div>
            </QuickCreatePopover>
          );
        })}
      </div>
    </Card>
  );
}

function WeekGrid({ refDate, eventsByDate, onEventClick, onCreateInterview, onCreateTask, onCreateCert }) {
  const days = useMemo(() => getWeekGrid(refDate), [refDate]);
  const todayStr = ymd(new Date());
  return (
    <Card className="border-[#0D1F3C]/8 overflow-hidden" data-testid="week-grid">
      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(380px, 1fr)" }}>
        {days.map((d, i) => {
          const ds = ymd(d);
          const evs = eventsByDate[ds] || [];
          const isToday = ds === todayStr;
          return (
            <div key={i} className={`border-r border-[#0D1F3C]/8 ${isToday ? "bg-blue-50/40" : "bg-white"}`} data-testid={`week-day-${ds}`}>
              <div className={`px-3 py-2 border-b border-[#0D1F3C]/8 ${isToday ? "bg-[#1B4FD8] text-white" : "bg-[#0D1F3C]/5"}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-80">{ITALIAN_DAYS[i]}</div>
                <div className={`text-base font-semibold ${isToday ? "text-white" : "text-[#0D1F3C]"}`}>{d.getDate()}</div>
              </div>
              <div className="p-2 space-y-1">
                {evs.length === 0 && <div className="text-[11px] text-[#0D1F3C]/30 text-center py-4">—</div>}
                {evs.map(ev => <EventChip key={ev.id} ev={ev} onClick={onEventClick} compact={false} />)}
                <QuickCreatePopover dateStr={ds}
                  onCreateInterview={onCreateInterview} onCreateTask={onCreateTask} onCreateCert={onCreateCert}>
                  <button className="w-full mt-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] text-[#0D1F3C]/40 hover:text-[#1B4FD8] hover:bg-[#1B4FD8]/5 rounded border border-dashed border-[#0D1F3C]/15 hover:border-[#1B4FD8]/40 transition-colors" data-testid={`week-add-${ds}`}>
                    <Plus size={11} weight="bold" /> Aggiungi
                  </button>
                </QuickCreatePopover>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DayGrid({ refDate, events, onEventClick, onCreateInterview, onCreateTask, onCreateCert }) {
  const ds = ymd(refDate);
  return (
    <Card className="border-[#0D1F3C]/8 p-6 max-w-3xl mx-auto" data-testid="day-grid">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#0D1F3C] capitalize">
          {refDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
        </h3>
        <QuickCreatePopover dateStr={ds}
          onCreateInterview={onCreateInterview} onCreateTask={onCreateTask} onCreateCert={onCreateCert}>
          <Button size="sm" className="gap-1.5 bg-[#1B4FD8] hover:bg-[#0D1F3C] text-xs h-8" data-testid="day-add-btn">
            <Plus size={12} weight="bold" /> Aggiungi
          </Button>
        </QuickCreatePopover>
      </div>
      {events.length === 0 ? (
        <div className="py-12 text-center">
          <CalendarCheck size={32} weight="duotone" className="mx-auto text-[#0D1F3C]/20 mb-2" />
          <p className="text-sm text-[#0D1F3C]/60">Nessun evento in questa giornata</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(ev => {
            const m = TYPE_META[ev.type] || TYPE_META.interview;
            const Icon = m.icon;
            return (
              <button key={ev.id} onClick={() => onEventClick(ev)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border ${m.border} ${m.bg} hover:opacity-90 transition text-left`}
                style={{ borderLeftWidth: 4, borderLeftColor: m.color }}
                data-testid={`day-event-${ev.id}`}>
                <Icon size={18} weight="duotone" style={{ color: m.color }} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${m.text}`}>{ev.title}</div>
                  {ev.metadata?.candidate_name && <div className="text-xs text-[#0D1F3C]/60">Candidato: {ev.metadata.candidate_name}</div>}
                  {ev.metadata?.employee_name && ev.type !== "interview" && <div className="text-xs text-[#0D1F3C]/60">Dipendente: {ev.metadata.employee_name}</div>}
                </div>
                {ev.time && <Badge className="bg-white text-[#0D1F3C] border border-[#0D1F3C]/10 font-mono text-xs">{ev.time}</Badge>}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
